import { useState } from "react";
import { Eye, Trash2, ChevronDown, ChevronUp, Download, Sparkles, Send, LinkIcon, Copy, ExternalLink, Loader2, Beaker } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface QuizSubmission {
  id: string;
  domain_input: string | null;
  created_at: string;
  quiz_score: number | null;
  quiz_band: string | null;
  quiz_answers: any;
  selected_competitors?: string[] | null;
  report_download_email?: string | null;
  report_downloaded_at?: string | null;
  report_token?: string | null;
  report_password?: string | null;
  enriched_at?: string | null;
  enriched_data?: any | null;
  pushed_to_crm_at?: string | null;
  is_test_data?: boolean;
  competitor_analysis?: any | null;
  session_id?: string | null;
}

interface MobileQuizCardProps {
  quiz: QuizSubmission;
  onViewAnswers: () => void;
  onDelete: () => void;
  getBandColor: (band: string | null) => any;
  getBandLabel: (band: string | null) => string;
  onGeneratePDF?: () => void;
  onEnrich?: () => void;
  onPushToCRM?: () => void;
  onViewEnriched?: () => void;
  onViewCompetitorAnalysis?: () => void;
  isEnriching?: boolean;
  isPushing?: boolean;
  submissionType?: 'quiz-only' | 'quiz-with-email' | 'quiz-with-lead';
  isTestRecord?: boolean;
}

export function MobileQuizCard({
  quiz,
  onViewAnswers,
  onDelete,
  getBandColor,
  getBandLabel,
  onGeneratePDF,
  onEnrich,
  onPushToCRM,
  onViewEnriched,
  onViewCompetitorAnalysis,
  isEnriching,
  isPushing,
  submissionType,
  isTestRecord,
}: MobileQuizCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();

  return (
    <Card className="p-4 space-y-3">
      {/* Test data & submission type badges */}
      <div className="flex gap-2 flex-wrap">
        {isTestRecord && (
          <Badge variant="test">
            <Beaker className="h-3 w-3 mr-1" />
            Test
          </Badge>
        )}
        <Badge variant={
          submissionType === 'quiz-with-lead' ? 'success' :
          submissionType === 'quiz-with-email' ? 'default' : 'secondary'
        }>
          {submissionType === 'quiz-with-lead' ? 'Quiz + Lead' :
           submissionType === 'quiz-with-email' ? 'Quiz + Email' : 'Quiz'}
        </Badge>
      </div>
      
      {/* Priority 1: Always visible */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base truncate">{quiz.domain_input || 'Brak domeny'}</h3>
          <p className="text-sm text-muted-foreground">
            {new Date(quiz.created_at).toLocaleDateString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
        <Badge variant={getBandColor(quiz.quiz_band)}>
          {getBandLabel(quiz.quiz_band)}
        </Badge>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold">{quiz.quiz_score || 0}</span>
        <span className="text-sm text-muted-foreground">punktów</span>
      </div>

      {/* Expandable competitors section */}
      {expanded && (
        <div className="space-y-3 pt-2 border-t">
          {quiz.selected_competitors && quiz.selected_competitors.length > 0 && (
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Konkurencja:</span>
              <div className="flex flex-wrap gap-1">
                {quiz.selected_competitors.map((comp: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {comp}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {quiz.report_download_email && (
            <div className="space-y-1">
              <span className="text-sm text-muted-foreground">Email (raport):</span>
              <p className="text-sm font-medium">{quiz.report_download_email}</p>
              {quiz.report_downloaded_at && (
                <p className="text-xs text-muted-foreground">
                  {new Date(quiz.report_downloaded_at).toLocaleDateString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          )}
          
          {quiz.report_token && quiz.report_password && (
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Link do raportu:</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const url = `${window.location.origin}/raport-zabezpieczony/${quiz.report_token}`;
                    navigator.clipboard.writeText(url);
                    toast({
                      title: "Skopiowano!",
                      description: "Link do raportu został skopiowany"
                    });
                  }}
                  className="flex-1"
                >
                  <LinkIcon className="h-3 w-3 mr-1" />
                  Kopiuj link
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    window.open(`/raport-zabezpieczony/${quiz.report_token}`, '_blank');
                  }}
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">
                  {quiz.report_password}
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(quiz.report_password || '');
                    toast({
                      title: "Skopiowano!",
                      description: "Hasło zostało skopiowane"
                    });
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1">
            {quiz.enriched_at && (
              <Badge variant="outline" className="text-xs">
                ✓ Enriched
              </Badge>
            )}
            {quiz.pushed_to_crm_at && (
              <Badge variant="outline" className="text-xs">
                ✓ CRM
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-2">
        <div className="flex items-center gap-2">
          {((quiz.selected_competitors && quiz.selected_competitors.length > 0) || quiz.report_download_email) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="flex-1"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Zwiń
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Szczegóły
                </>
              )}
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={onViewAnswers} className="flex-1">
            <Eye className="h-4 w-4 mr-2" />
            Odpowiedzi
          </Button>
          
          {onViewEnriched && quiz.enriched_data && (
            <Button variant="outline" size="sm" onClick={onViewEnriched} className="flex-1">
              <Eye className="h-4 w-4 mr-2" />
              Dane firmy
            </Button>
          )}

          {onViewCompetitorAnalysis && quiz.competitor_analysis && (
            <Button variant="outline" size="sm" onClick={onViewCompetitorAnalysis} className="flex-1">
              <Eye className="h-4 w-4 mr-2" />
              Analiza AI
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={onDelete} className="flex-1">
            <Trash2 className="h-4 w-4 mr-2" />
            Usuń
          </Button>
        </div>
        
        {onGeneratePDF && (
          <Button variant="default" size="sm" onClick={onGeneratePDF} className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Pobierz raport PDF
          </Button>
        )}
        
        <div className="flex items-center gap-2">
          {onEnrich && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEnrich}
              disabled={isEnriching}
              className="flex-1"
            >
              {isEnriching ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Enrich AI
            </Button>
          )}
          {onPushToCRM && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPushToCRM}
              disabled={isPushing}
              className="flex-1"
            >
              {isPushing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Salesflare
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
